import type { ScreenerOIDataPoint } from '@/types'
import { formatUtc, isPlottableOI, oiSeriesWithGaps } from './chartUtils'

/** Presentation metadata only: a filled value is never a new market observation. */
export interface OIDisplayPoint extends ScreenerOIDataPoint {
  filledFromTimestamp?: number
}

export function isDisplayableOI(point: OIDisplayPoint): boolean {
  return Number.isFinite(point.value) && point.value >= 0
    && (point.filledFromTimestamp != null || isPlottableOI(point))
}

export function filledOIDescription(point: OIDisplayPoint): string | null {
  return point.filledFromTimestamp == null ? null
    : `Filled with last data received at ${formatUtc(point.filledFromTimestamp * 1000)}`
}

export function buildOIPlot(
  rawPoints: ScreenerOIDataPoint[],
  interval: string,
  fillThrough?: number | null,
): {
  points: OIDisplayPoint[]
  series: Array<[number, number | null]>
  latestFilled: OIDisplayPoint | null
} {
  const byTimestamp = new Map<number, ScreenerOIDataPoint>()
  const observed = (point: ScreenerOIDataPoint) => isPlottableOI(point) && Number.isFinite(point.value) && point.value >= 0
  for (const point of rawPoints) {
    if (!Number.isFinite(point.timestamp)) continue
    const previous = byTimestamp.get(point.timestamp)
    // A missing placeholder must never replace a received value in the same bucket.
    if (!previous || (observed(point) && (!observed(previous)
      || (point.observedAt ?? point.timestamp) >= (previous.observedAt ?? previous.timestamp)))) {
      byTimestamp.set(point.timestamp, point)
    }
  }
  const sorted = [...byTimestamp.values()].sort((a, b) => a.timestamp - b.timestamp)
  if (interval !== '5s') return { points: sorted, series: oiSeriesWithGaps(sorted, interval), latestFilled: null }

  const points: OIDisplayPoint[] = []
  let lastObserved: ScreenerOIDataPoint | null = null
  let previousTimestamp: number | null = null
  let generated = 0
  const MAX_GENERATED_POINTS = 10_000
  const appendFilled = (timestamp: number) => {
    if (!lastObserved) return
    const receivedAt = lastObserved.observedAt
    points.push({
      ...lastObserved,
      timestamp,
      quality: 'display_filled',
      sampleKind: 'display',
      filledFromTimestamp: receivedAt != null && Number.isFinite(receivedAt) ? receivedAt : lastObserved.timestamp,
    })
    generated += 1
  }
  const fillRange = (start: number, end: number) => {
    if (!lastObserved || start > end) return
    const count = Math.floor((end - start) / 5) + 1
    if (generated + count <= MAX_GENERATED_POINTS) {
      for (let index = 0; index < count; index += 1) appendFilled(start + index * 5)
    } else {
      // Long outages need only flat-line endpoints, not millions of copies.
      appendFilled(start)
      if (count > 1) appendFilled(start + (count - 1) * 5)
    }
  }
  for (const point of sorted) {
    if (previousTimestamp != null && point.timestamp - previousTimestamp > 7.5) {
      fillRange(previousTimestamp + 5, point.timestamp - 5)
    }
    if (observed(point)) {
      lastObserved = point
      points.push(point)
    } else if (lastObserved) {
      appendFilled(point.timestamp)
    } else {
      // No backward fill: data that predates the first observation stays unknown.
      points.push(point)
    }
    previousTimestamp = point.timestamp
  }
  if (previousTimestamp != null && fillThrough != null && Number.isFinite(fillThrough)) {
    // The server supplies a completed-bucket watermark, capped to the requested
    // historical range. Never use the browser clock to extend past that range.
    fillRange(previousTimestamp + 5, Math.floor(fillThrough / 5) * 5)
  }
  const latest = points[points.length - 1]
  return {
    points,
    series: points.map((point) => [point.timestamp * 1000, isDisplayableOI(point) ? point.value : null]),
    latestFilled: latest?.filledFromTimestamp != null ? latest : null,
  }
}
